// src/features/system/admin/components/EmailTemplatesPreview.tsx
//
// Admin panel for previewing email templates rendered by Rust comms.
// Renders the template inside an iframe via the BFF preview endpoint.

import React, { useState, useMemo } from 'react';

/* ─── Constants ───────────────────────────────────────────── */

const TEMPLATES = [
  { key: 'welcome',        label: 'Welcome' },
  { key: 'password_reset',  label: 'Password Reset' },
  { key: 'notification',    label: 'Notification' },
] as const;

const LANGUAGES = [
  { key: 'en', label: 'EN' },
  { key: 'es', label: 'ES' },
] as const;

type TemplateName = (typeof TEMPLATES)[number]['key'];
type LangCode     = (typeof LANGUAGES)[number]['key'];

/* ─── Component ───────────────────────────────────────────── */

export default function EmailTemplatesPreview() {
  const [template, setTemplate] = useState<TemplateName>('welcome');
  const [lang, setLang]         = useState<LangCode>('en');

  // Build the preview URL — the iframe loads directly from the BFF.
  // Session cookies travel automatically.
  const previewUrl = useMemo(
    () => `/bff/orchestrator/comms/templates/preview?template=${template}&lang=${lang}`,
    [template, lang],
  );

  return (
    <div className="space-y-4">
      {/* ── Controls ── */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Template selector */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-500 uppercase tracking-wider">Template</span>
          <div className="flex gap-1">
            {TEMPLATES.map(t => (
              <button
                key={t.key}
                onClick={() => setTemplate(t.key)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  template === t.key
                    ? 'bg-emerald-500/20 text-emerald-400 font-semibold ring-1 ring-emerald-500/40'
                    : 'text-neutral-400 hover:text-neutral-200 hover:border-neutral-600'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Language toggle */}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-neutral-500 uppercase tracking-wider">Lang</span>
          <div className="flex gap-1">
            {LANGUAGES.map(l => (
              <button
                key={l.key}
                onClick={() => setLang(l.key)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  lang === l.key
                    ? 'bg-emerald-500/20 text-emerald-400 font-semibold ring-1 ring-emerald-500/40'
                    : 'text-neutral-400 hover:text-neutral-200 hover:border-neutral-600'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Preview iframe ── */}
      <div className="rounded-lg pac-glass overflow-hidden">
        <iframe
          key={previewUrl}
          src={previewUrl}
          title={`Email preview: ${template} (${lang})`}
          className="w-full border-0"
          style={{ minHeight: 720, background: '#0a0a0a' }}
          sandbox="allow-same-origin"
        />
      </div>

      {/* ── Info note ── */}
      <p className="text-xs text-neutral-600">
        Previews are rendered server-side with mock data. Actual emails use real user details.
      </p>
    </div>
  );
}
